import React from 'react';
import { Field, useFormikContext } from 'formik';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import configService from '../../service/config.service';
import { InputUI, FormErrorUI, FormLabelUI, AutoCompleteSelectUI, CheckboxUI, ButtonUI } from '../Interface';

const smtpEncryptionOptions = [
  { value: 'SSL', label: 'SSL' },
  { value: 'TLS', label: 'TLS' },
  { value: 'STARTTLS', label: 'STARTTLS' },
];

const EmailSettingFields = ({ viewField }) => {
  const { values, errors, setFieldValue, validateField } = useFormikContext();

  const handleTestClick = () => {
    addNotification({
      content: 'Az SMTP kapcsolat tesztelése még nincs bekötve a backend végpontra.',
      type: NotificationType.INFO,
    });
  };

  const handleConnectionTestClick = async () => {
    const testData = {
      emailSendingEnabled: values.emailSendingEnabled ?? false,
      smtpHost: values.smtpHost ?? null,
      smtpPort: values.smtpPort === '' || values.smtpPort === null ? null : parseInt(values.smtpPort, 10),
      smtpEncryption: values.smtpEncryption ?? null,
      smtpUsername: values.smtpUsername ?? null,
      smtpPassword: values.smtpPassword ?? null,
      emailSenderName: values.emailSenderName ?? null,
      emailSenderAddress: values.emailSenderAddress ?? null,
      defaultReplyToAddress: values.defaultReplyToAddress ?? null,
      emailSendCopyToSender: values.emailSendCopyToSender ?? false,
    };

    try {
      await configService.testConnection(testData);
      addNotification({
        content: 'Sikeres kapcsolat teszt!',
        type: NotificationType.SUCCESS,
      });
    } catch (error) {
      addNotification({
        content: 'Sikertelen kapcsolat teszt!',
        type: NotificationType.ERROR,
      });
    }
  };

  return (
    <div className="grid grid-cols-6 gap-x-4 mx-4 mb-5">
      <div className="col-span-6 text-sm font-medium text-labelColor mt-2 mb-1">SMTP kapcsolat</div>

      <div className="md:col-span-3 xl:col-span-2 col-span-6">
        <Field
          type="text"
          name="smtpHost"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="SMTP szerver" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.smtpHost} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.smtpHost && values.smtpHost !== '' }}
        />
      </div>
      <div className="md:col-span-3 xl:col-span-1 col-span-6">
        <Field
          type="number"
          name="smtpPort"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="SMTP port" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.smtpPort} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.smtpPort !== null && values.smtpPort !== '' }}
        />
      </div>
      <div className="md:col-span-3 xl:col-span-1 col-span-6">
        <Field
          type="autocomplete"
          name="smtpEncryption"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Titkosítás típusa" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.smtpEncryption} />}
          onChange={(_e, newVal) => {
            setFieldValue('smtpEncryption', newVal?.value ?? null).then(() => {
              validateField('smtpEncryption');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.smtpEncryption !== '' }}
          selectedValue={values.smtpEncryption}
          dataset={smtpEncryptionOptions}
        />
      </div>
      <div className="md:col-span-3 xl:col-span-1 col-span-6">
        <Field
          type="text"
          name="smtpUsername"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Felhasználónév" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.smtpUsername} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.smtpUsername && values.smtpUsername !== '' }}
        />
      </div>
      <div className="md:col-span-3 xl:col-span-1 col-span-6">
        <Field
          type="password"
          name="smtpPassword"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Jelszó" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.smtpPassword} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.smtpPassword && values.smtpPassword !== '' }}
        />
      </div>

      <div className="col-span-6 mt-2">
        <ButtonUI
          text="Kapcsolat tesztelése"
          type="button"
          className="bg-primary"
          disabled={viewField}
          onClick={handleConnectionTestClick}
        />
      </div>

      <div className="col-span-6 text-sm font-medium text-labelColor mt-4 mb-1">Küldő adatok</div>

      <div className="md:col-span-3 xl:col-span-2 col-span-6">
        <Field
          type="text"
          name="emailSenderName"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text='Küldő neve (pl. "Quantelligen Kft")' />}
          variant="standard"
          helperText={<FormErrorUI message={errors.emailSenderName} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.emailSenderName && values.emailSenderName !== '' }}
        />
      </div>
      <div className="md:col-span-3 xl:col-span-2 col-span-6">
        <Field
          type="text"
          name="emailSenderAddress"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Küldő e-mail címe" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.emailSenderAddress} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.emailSenderAddress && values.emailSenderAddress !== '' }}
        />
      </div>

      <div className="col-span-6 text-sm font-medium text-labelColor mt-4 mb-1">Alapértelmezett válaszcím</div>

      <div className="md:col-span-3 xl:col-span-2 col-span-6">
        <Field
          type="text"
          name="defaultReplyToAddress"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text='Alapértelmezett válaszcím (pl. info@ceg.hu)' />}
          variant="standard"
          helperText={<FormErrorUI message={errors.defaultReplyToAddress} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.defaultReplyToAddress && values.defaultReplyToAddress !== '' }}
        />
      </div>

      <div className="col-span-6 text-sm font-medium text-labelColor mt-4 mb-1">Küldési beállítások</div>

      <div className="col-span-6 md:col-span-3 pt-2">
        <Field
          name="emailSendingEnabled"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="E-mail küldés engedélyezése" />}
          disabled={viewField}
        />
      </div>
      <div className="col-span-6 md:col-span-3 pt-2">
        <Field
          name="emailSendCopyToSender"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Másolat küldése a feladónak" />}
          disabled={viewField}
        />
      </div>
    </div>
  );
};

export default EmailSettingFields;
